// SessionSummaryCard — the at-a-glance header for a refill session's detail
// page. It resolves the related grow / reservoir / recipe (each lookup is
// independent and degrades gracefully when the id is missing or the fetch is
// still in flight) and lays out the session's metadata plus a compact
// "predicted vs actual" comparison.
//
// Lives next to the other refill detail widgets so the detail page can stay a
// thin orchestrator. Read-only: it never mutates, it only links out to the
// owning grow.

import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";

import { useGrow } from "@/lib/api/grows";
import { useRecipe } from "@/lib/api/recipes";
import { useReservoir } from "@/lib/api/reservoirs";
import { usePreferences } from "@/lib/preferences";
import {
  ecUnitLabel,
  formatEc,
  formatTemp,
  tempUnitLabel,
} from "@/lib/units";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { RefillSession } from "@/lib/api/types";

export function SessionSummaryCard({
  session,
  formatTime,
}: {
  session: RefillSession;
  formatTime: (s: string) => string;
}) {
  const s = session;
  const { temperatureUnit, ecUnit } = usePreferences();

  const grow = useGrow(s.grow_id);
  // The reservoir is reached through the grow, so it can only resolve once the
  // grow has loaded and actually carries a reservoir_id.
  const reservoir = useReservoir(grow.data?.reservoir_id);
  // recipe_version is the *pinned* version this session ran against; pass it
  // through so the lookup returns the exact recipe snapshot.
  const recipe = useRecipe(s.recipe_id, s.recipe_version);

  const growLabel = grow.data
    ? [
        grow.data.species,
        grow.data.system_type,
        grow.data.count != null
          ? `${grow.data.count} plant${grow.data.count === 1 ? "" : "s"}`
          : undefined,
      ]
        .filter(Boolean)
        .join(" · ")
    : undefined;

  // Recipe label: prefer "Name v3"; fall back to just "v3" when the recipe
  // can't be resolved but we still know the version; otherwise show nothing.
  const recipeLabel = (() => {
    const v =
      s.recipe_version != null ? `v${s.recipe_version}` : undefined;
    if (recipe.data?.name) return v ? `${recipe.data.name} ${v}` : recipe.data.name;
    if (v) return v;
    return s.recipe_id ? "—" : undefined;
  })();

  const hasComparison = !!s.predicted_state && !!s.post_pour_state;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
          Summary
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Grow">
            {grow.isLoading ? (
              <span className="text-muted-foreground">loading…</span>
            ) : growLabel ? (
              <Link
                to="/grows/$growId"
                params={{ growId: s.grow_id }}
                className="font-medium hover:underline"
              >
                {growLabel}
              </Link>
            ) : (
              <span className="text-muted-foreground">unknown</span>
            )}
          </Field>

          {reservoir.data?.name && (
            <Field label="Reservoir">
              <span className="font-medium">{reservoir.data.name}</span>
            </Field>
          )}

          {recipeLabel && (
            <Field label="Recipe">
              <span className="font-medium">{recipeLabel}</span>
            </Field>
          )}

          <Field label="Trigger">
            <span className="font-medium">{s.trigger || "—"}</span>
          </Field>

          {s.decision_type && (
            <Field label="Decision">
              <span className="font-medium">{s.decision_type}</span>
            </Field>
          )}

          {s.batch_size_gal != null && (
            <Field label="Batch size">
              <span className="font-medium tabular-nums">
                {s.batch_size_gal} gal
              </span>
            </Field>
          )}

          <Field label="Started">
            <span className="tabular-nums">{formatTime(s.started_at)}</span>
          </Field>

          <Field label={s.voided_at ? "Voided" : "Closed"}>
            {s.voided_at ? (
              <span className="tabular-nums">
                {formatTime(s.voided_at)}
                {s.voided_reason ? ` · ${s.voided_reason}` : ""}
              </span>
            ) : s.closed_at ? (
              <span className="tabular-nums">{formatTime(s.closed_at)}</span>
            ) : (
              <span className="text-muted-foreground">open</span>
            )}
          </Field>
        </dl>

        {hasComparison && (
          <div className="space-y-2">
            <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Predicted vs actual
            </h3>
            <div className="overflow-hidden rounded-md border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/40 text-xs text-muted-foreground">
                    <th className="px-3 py-1.5 text-left font-medium">metric</th>
                    <th className="px-3 py-1.5 text-right font-medium">
                      predicted
                    </th>
                    <th className="px-3 py-1.5 text-right font-medium">actual</th>
                    <th className="px-3 py-1.5 text-right font-medium">Δ</th>
                  </tr>
                </thead>
                <tbody>
                  <ComparisonRow
                    label="pH"
                    predicted={s.predicted_state?.ph}
                    actual={s.post_pour_state?.ph}
                    format={(n) => n.toFixed(2)}
                  />
                  <ComparisonRow
                    label={ecUnitLabel(ecUnit)}
                    predicted={s.predicted_state?.ec}
                    actual={s.post_pour_state?.ec}
                    format={(n) => formatEc(n, ecUnit)}
                    // EC deltas are reported in the display unit too.
                    formatDelta={(n) => formatEc(n, ecUnit)}
                  />
                  <ComparisonRow
                    label={`temp ${tempUnitLabel(temperatureUnit)}`}
                    predicted={s.predicted_state?.temp_c}
                    actual={s.post_pour_state?.temp_c}
                    format={(c) => formatTemp(c, temperatureUnit, 1)}
                    // A delta in °C and °F differs only by the scale factor (no
                    // 32° offset), so reuse formatTemp on the raw C difference.
                    formatDelta={(c) => formatTemp(c, temperatureUnit, 1)}
                  />
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd>{children}</dd>
    </div>
  );
}

function ComparisonRow({
  label,
  predicted,
  actual,
  format,
  formatDelta,
}: {
  label: string;
  predicted: number | undefined;
  actual: number | undefined;
  format: (n: number) => string;
  // Deltas can need a different scale than absolute values (e.g. a temp delta
  // has no offset); defaults to `format` when omitted.
  formatDelta?: (n: number) => string;
}) {
  const fmtDelta = formatDelta ?? format;
  const haveBoth = predicted != null && actual != null;
  const delta = haveBoth ? (actual as number) - (predicted as number) : undefined;

  return (
    <tr className="border-b last:border-b-0">
      <td className="px-3 py-1.5 text-muted-foreground">{label}</td>
      <td className="px-3 py-1.5 text-right tabular-nums">
        {predicted != null ? format(predicted) : "—"}
      </td>
      <td className="px-3 py-1.5 text-right tabular-nums">
        {actual != null ? format(actual) : "—"}
      </td>
      <td className="px-3 py-1.5 text-right tabular-nums">
        {delta === undefined ? (
          "—"
        ) : (
          <span
            className={
              delta > 0
                ? "text-amber-600 dark:text-amber-400"
                : delta < 0
                  ? "text-sky-600 dark:text-sky-400"
                  : "text-muted-foreground"
            }
          >
            {delta > 0 ? "+" : ""}
            {fmtDelta(delta)}
          </span>
        )}
      </td>
    </tr>
  );
}
